import { useStyles } from '@archipro-design/aria';
import { useCallback, useEffect, useRef } from 'react';
import { NoSearchResult } from '@modules/professional/component/contact-tab/NoSearchResult';
import type {
    LocationDataType,
    ProfessionalInitialType,
} from '@modules/professional/type/contact-page-types';
import * as S from './ContactPage.desktop.style';
import { LocationDetail } from './LocationDetail.desktop';
import { LocationListItem } from './LocationListItem';

interface LocationsProps {
    locations: LocationDataType[];
    hoveredLocation?: LocationDataType;
    clickedLocation?: LocationDataType;
    onListItemClick: (location: LocationDataType) => void;
    onListItemHover: (location: LocationDataType) => void;
    onLocationDetailClose: () => void;
    professional: ProfessionalInitialType;
    originLocationCount: number;
    disableEnquire?: boolean;
    disableContactViewProfile?: boolean;
    autoRevealAddress?: boolean;
}

export const LocationList: React.FC<LocationsProps> = ({
    locations,
    professional,
    hoveredLocation,
    clickedLocation,
    onListItemClick,
    onListItemHover,
    onLocationDetailClose,
    originLocationCount,
    disableEnquire,
    disableContactViewProfile,
    autoRevealAddress = false,
}) => {
    const { css } = useStyles();
    const listSectionRef = useRef<HTMLDivElement>(null);

    const locationDetail =
        originLocationCount === 1 && locations.length === 1
            ? locations[0]
            : clickedLocation;

    const scrollToClickedLocation = useCallback(() => {
        if (listSectionRef.current && locations.length >= 3) {
            const rowIndex = locations.findIndex(
                (location) => location.ID === clickedLocation?.ID,
            );

            const rows = listSectionRef.current.children[0];
            const rowHeight = rows?.children[0]?.clientHeight;

            if (rowIndex >= 0 && rowHeight) {
                const clickedRowOffsetTop = (
                    rows.children[rowIndex * 2] as HTMLElement
                ).offsetTop;

                const firstRowOffsetTop = (rows.children[0] as HTMLElement)
                    .offsetTop;

                const scrollTop =
                    clickedRowOffsetTop - firstRowOffsetTop > 0
                        ? clickedRowOffsetTop - firstRowOffsetTop
                        : rowIndex * (rowHeight + 3) + 3;

                listSectionRef.current.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth',
                });
            }
        }
    }, [clickedLocation, locations]);

    const locationsForList = clickedLocation
        ? locations.filter((location) => location.ID !== clickedLocation.ID)
        : locations;

    useEffect(() => {
        // wait scroll to the top of the map finish first
        setTimeout(scrollToClickedLocation, 500);
    }, [scrollToClickedLocation]);

    return (
        <>
            {locationDetail && (
                <LocationDetail
                    location={locationDetail}
                    professional={professional}
                    showCloseButton={locations.length > 1}
                    onLocationDetailClose={onLocationDetailClose}
                    disableEnquire={disableEnquire}
                    disableContactViewProfile={disableContactViewProfile}
                    autoRevealAddress={autoRevealAddress}
                />
            )}
            {originLocationCount > 1 && (
                <div
                    className={css(S.LocationListSection)}
                    ref={listSectionRef}
                >
                    {locationsForList.length > 0 ? (
                        <div className={css(S.LocationListWrapper)}>
                            {locationsForList.map((location, index) => (
                                <LocationListItem
                                    location={location}
                                    key={index}
                                    professional={professional}
                                    isHovered={
                                        location.ID === hoveredLocation?.ID
                                    }
                                    isClicked={
                                        location.ID === clickedLocation?.ID
                                    }
                                    onListItemClick={onListItemClick}
                                    onListItemHover={onListItemHover}
                                />
                            ))}
                        </div>
                    ) : (
                        <NoSearchResult />
                    )}
                </div>
            )}
        </>
    );
};
